import type { DialogLoader } from './lazy-dialog.service';
// 以下一律使用 import type。此檔的職責是描述「怎麼載入」，
// 不得產生任何 runtime 依賴 —— 只要有一行靜態 import，
// 該彈窗的整棵功能樹就會回到 initial bundle。
import type { ChatComponent } from 'src/app/pages/chat/chat.component';
import type { CustomizedTimeDialogComponent } from 'src/app/pages/wallet-history/customized-time-dialog/customized-time-dialog.component';
import type { KycDialogComponent } from 'src/app/shared/components/kyc-dialog/kyc-dialog.component';
import type { Verify2FADialogComponent } from 'src/app/shared/components/verify-2fa/verify-2fa.dialog';
import type { LoginDialogComponent } from 'src/app/pages/login-dialog/login-dialog';
import type { WalletDialogComponent } from 'src/app/pages/wallet/wallet-dialog';

/**
 * 延遲載入彈窗的載入器清單。
 *
 * 集中在此有兩個用意：
 *   1. 呼叫端不必內嵌 `() => import('...').then(m => m.XComponent)` 的雜訊
 *   2. 這份清單本身就是「哪些彈窗已 lazy 化」的稽核依據
 *
 * 搭配 {@link LazyDialogService} 使用：
 *
 * ```ts
 * this.lazyDialog.open(LoginDialog, config);
 * ```
 */

/** 登入 / 註冊 / 重設密碼彈窗 */
export const LoginDialog: DialogLoader<LoginDialogComponent> = () =>
  import('src/app/pages/login-dialog/login-dialog').then(m => m.LoginDialogComponent);

/** 錢包彈窗（存提款、地址管理、首儲等） */
export const WalletDialog: DialogLoader<WalletDialogComponent> = () =>
  import('src/app/pages/wallet/wallet-dialog').then(m => m.WalletDialogComponent);

/**
 * KYC 表單彈窗（第三方註冊流程）
 *
 * 註：`pages/settings/verification` 亦使用此元件，但該處位於 settings 的 lazy chunk 內，
 * 維持靜態 import 即可 —— 需要斷開的是 `ThirdAuthM8Service` 這條，
 * 該服務為 `providedIn: 'root'` 且被 `app.component` 注入，屬 eager。
 */
export const KycDialog: DialogLoader<KycDialogComponent> = () =>
  import('src/app/shared/components/kyc-dialog/kyc-dialog.component').then(m => m.KycDialogComponent);

/** 二階段驗證彈窗。同上，`pages/settings/security` 的靜態 import 不需更動。 */
export const Verify2FADialog: DialogLoader<Verify2FADialogComponent> = () =>
  import('src/app/shared/components/verify-2fa/verify-2fa.dialog').then(m => m.Verify2FADialogComponent);

/**
 * 自訂時間區間彈窗（帳務紀錄篩選）。走 {@link LazyDialogService.openPopup}。
 *
 * 這條是 Material datepicker（102 KB）進入首屏的唯一來源：
 * `CustomizedTimeService` 為 `providedIn:'root'`，被 `CustomizeFormModule` 的
 * customize-select 注入，而該模組列在 `app.module` 的 imports。
 */
export const CustomizedTimeDialog: DialogLoader<CustomizedTimeDialogComponent> = () =>
  import('src/app/pages/wallet-history/customized-time-dialog/customized-time-dialog.component').then(
    m => m.CustomizedTimeDialogComponent,
  );

/**
 * 聊天面板（H5 全螢幕彈窗路徑）。走 {@link LazyDialogService.openPopup}。
 *
 * Web 版的內嵌路徑不走這裡 —— 那條由 `ChatPanelComponent` 的 `@defer` 負責，
 * 兩者最終載入的是同一個 chunk。
 */
export const ChatDialog: DialogLoader<ChatComponent> = () =>
  import('src/app/pages/chat/chat.component').then(m => m.ChatComponent);
